import { useMemo, useRef } from 'react';
import { Observable, of, range, Subscription, throwError, timer, defer } from 'rxjs';
import { fromPromise } from 'rxjs/internal-compatibility';
import {
  catchError,
  delay,
  filter,
  flatMap,
  repeatWhen,
  retryWhen,
  switchMap,
  take,
  zip
} from 'rxjs/operators';
import { hasDuplicateItems } from '../../../core/utils/array';
import { getErrorMessageFromObj } from '../../../core/utils/request';
import { QUERY_EXECUTION_RES_RETRY_COUNT, RETRIABLE_STATUSES } from '../constants/constants';
import { QueryExecutionResponse, QueryExecutionStatusEnum } from '../interfaces/workbench';
import { getQueryExecutionResponseFromRawData } from '../utils/workbench';

export function useQueryExecutioner({
  sourceId,
  query,
  onExecutionStart,
  onExecutionEnd,
  queryExecutionResponse,
  onQueryExecutionResponseChange,
  stopQueryExecution$,
  getQueryExecutionTaskId$,
  getQueryExecutionTaskResponse$
}: {
  sourceId: number;
  query: string;
  queryExecutionResponse: QueryExecutionResponse;
  onQueryExecutionResponseChange: (res: QueryExecutionResponse) => any;
  onExecutionStart?: Function;
  onExecutionEnd?: Function;
  stopQueryExecution$: (taskId: number) => Promise<any>;
  getQueryExecutionTaskId$: (sourceId: number, query: string) => Promise<any>;
  getQueryExecutionTaskResponse$: (taskId: number, query?: string) => Promise<any>;
}) {
  const taskIdRef = useRef(null);
  const queryExecutionSub = useRef<Subscription>(Subscription.EMPTY);

  const queryExecutionStatus = queryExecutionResponse?.status;
  const queryRunErrorMessage = queryExecutionResponse?.error;
  const lastRunActionTime = queryExecutionResponse?.data?.runTime;
  const hasMoreResults = queryExecutionResponse?.data?.hasMoreResults;
  const noResultOutput =
    !!queryExecutionResponse?.data?.output &&
    Object.keys(queryExecutionResponse.data.output).length === 0;

  const isExecutionRequestInProgress = useMemo(
    () =>
      typeof queryExecutionStatus !== 'undefined' &&
      queryExecutionStatus !== null &&
      ![
        QueryExecutionStatusEnum.SUCCESS,
        QueryExecutionStatusEnum.FAILED,
        QueryExecutionStatusEnum.CANCEL_REQUESTED,
        QueryExecutionStatusEnum.CANCELLED
      ].includes(queryExecutionStatus),
    [queryExecutionStatus]
  );

  const runningQuery = useMemo(
    () =>
      typeof queryExecutionStatus !== 'undefined' &&
      queryExecutionStatus !== null &&
      [
        QueryExecutionStatusEnum.REQUEST_ID,
        QueryExecutionStatusEnum.RUNNING,
        QueryExecutionStatusEnum.QUEUED
      ].includes(queryExecutionStatus),
    [queryExecutionStatus]
  );

  const hasDuplicateColumns = useMemo(() => {
    if (!queryExecutionResponse?.data?.output) {
      return false;
    }

    const headerNames = queryExecutionResponse.data.output.headers.map(
      (header: any) => header.name
    );

    return hasDuplicateItems(headerNames);
  }, [queryExecutionResponse]);

  const execute = () => {
    queryExecutionSub.current.unsubscribe();

    onExecutionStart && onExecutionStart();

    onQueryExecutionResponseChange({
      status: QueryExecutionStatusEnum.REQUEST_ID
    });

    queryExecutionSub.current = fromPromise(getQueryExecutionTaskId$(sourceId, query))
      .pipe(
        switchMap((taskId: number) => {
          taskIdRef.current = taskId;

          return defer(() => fromPromise(getQueryExecutionTaskResponse$(taskId, query))).pipe(
            repeatWhen((notifier: Observable<void>) => notifier.pipe(delay(1000))),
            retryWhen((errors: Observable<any>) =>
              errors.pipe(
                zip(range(1, QUERY_EXECUTION_RES_RETRY_COUNT + 1)),
                flatMap(([error, i]) => {
                  if (
                    !RETRIABLE_STATUSES[error.status] ||
                    i === QUERY_EXECUTION_RES_RETRY_COUNT + 1
                  ) {
                    return throwError(error);
                  }

                  return timer(2 * 1000);
                })
              )
            ),
            filter((res: any) => {
              onQueryExecutionResponseChange(getQueryExecutionResponseFromRawData(res.data));

              if (['SUCCESS', 'FAILED'].includes(res.data.status) && onExecutionEnd) {
                onExecutionEnd();
              }

              return ['SUCCESS', 'FAILED', 'CANCELLED'].includes(res.data.status);
            }),
            take(1)
          );
        }),
        catchError(err => {
          onQueryExecutionResponseChange({
            error: getErrorMessageFromObj(err),
            status: QueryExecutionStatusEnum.ERRORED
          });
          return of(null);
        })
      )
      .subscribe();
  };

  const stop = () => {
    if (!isExecutionRequestInProgress) {
      return;
    }

    queryExecutionSub.current.unsubscribe();

    onQueryExecutionResponseChange({
      status: QueryExecutionStatusEnum.REQUEST_CANCEL
    });

    onExecutionEnd();

    stopQueryExecution$(taskIdRef.current).catch(() => {});
  };

  const reset = () => {
    stop();
    onQueryExecutionResponseChange(null);
  };

  return {
    reset,
    execute,
    stop,
    queryRunErrorMessage,
    hasDuplicateColumns,
    runningQuery,
    queryExecutionStatus,
    noResultOutput,
    lastRunActionTime,
    hasMoreResults
  };
}
